/*
 * voucherHelpers.js — which shopping cart lines an applied voucher (coupon) discounts in WebPOS.
 *
 * WebPOS keeps the voucher discount on the cart lines (itemTypeData.discount / voucherId / voucherCode),
 * the cart itself only remembers which voucher is applied. Lines added to the cart after the voucher was
 * applied therefore have to be stamped by WebPOS, the add-to-cart endpoints do not do it.
 */
import {usageTypeCodes} from "@/enums/usageTypeCodes";
import {entityTypes} from "@/enums/entityTypes";
import {MarkupTypeCodes} from "@/enums/markupTypeCodes";

function hasVoucher(voucher) {
  return !!voucher && voucher.id > 0;
}

// generic and customer specific vouchers discount every line, product specific ones only the matching item type / group
export function isItemEligibleForVoucher(voucher, item) {
  if (!hasVoucher(voucher) || !item || !item.itemTypeData) {
    return false;
  }
  if (voucher.usageType === usageTypeCodes.GENERIC || voucher.usageType === usageTypeCodes.CUSTOMER_SPECIFIC) {
    return true;
  }

  return (voucher.entityTypeID === entityTypes.ItemTypes && item.itemTypeData.itemTypeID === voucher.entityID)
    || (voucher.entityTypeID == entityTypes.ItemGroups && item.itemTypeData.itemGroupID == voucher.entityID);
}

// a voucher is always applied as a relative discount on the line
export function applyVoucherToItem(item, voucher) {
  item.itemTypeData.discount = -voucher.discount;
  item.itemTypeData.markupType = MarkupTypeCodes.RELATIVE;
  item.itemTypeData.voucherId = voucher.id;
  item.itemTypeData.voucherCode = voucher.code;
  return item;
}

export function removeVoucherFromItem(item) {
  item.itemTypeData.discount = 0;
  item.itemTypeData.markupType = MarkupTypeCodes.RELATIVE;
  item.itemTypeData.voucherId = null;
  item.itemTypeData.voucherCode = "";
  return item;
}

export function getCartItemIds(items) {
  return (items || []).map(item => item.id);
}

// copies of the lines added since previousItemIds was taken that the voucher applies to, already carrying the voucher discount.
// lines that were in the cart before are left alone, so a voucher removed from a single line is not brought back
// and an existing line that only had its quantity increased keeps its discount as it is.
export function getNewItemsWithVoucherDiscount(voucher, items, previousItemIds) {
  if (!hasVoucher(voucher)) {
    return [];
  }
  const previousIds = new Set(previousItemIds || []);

  return (items || [])
    .filter(item => !previousIds.has(item.id))
    .filter(item => !(item.itemTypeData && item.itemTypeData.voucherId > 0))
    .filter(item => isItemEligibleForVoucher(voucher, item))
    .map(item => applyVoucherToItem(JSON.parse(JSON.stringify(item)), voucher));
}
